/* Copyright (c) 2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useCustomer } from "~/context/User/Customer";
import { usePDCustomers } from "~/hooks/usePDCustomers";
import { RouteParams } from "~/models";
import {
  PagerDutyBusinessService,
  PDTenant,
} from "~/models/pagerDutyBusinessService";
import { generateActionSuccessText, useAxios } from "~/tools";
import { ToolsAndIntegrations as ToolsAndIntegrationsType } from "../BasicCustomerInfo/types";
import { validateDate, tenantOptions } from "./constants";

type AddPagerDutyBusinessServiceProps = {
  refetch: () => void;
  fetchCustomer: () => void;
};

type FormValues = {
  tenant: "PD US" | "PD EU" | "";
  reportingStartDate: string;
  reportingEndDate: string;
  businessServiceId: string;
};

function AddPagerDutyBusinessService({
  refetch,
  fetchCustomer,
}: AddPagerDutyBusinessServiceProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm<FormValues>({
    defaultValues: {
      tenant: "",
      businessServiceId: "",
      reportingStartDate: "",
      reportingEndDate: "",
    },
  });
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();
  const selectedTenant = watcher.tenant;

  const state = useParams<RouteParams>();
  const { customerId } = state;

  const { customer } = useCustomer();
  const {
    customers,
    loading: customersLoading,
    fetchCustomers,
  } = usePDCustomers();

  const filteredCustomers = React.useMemo(() => {
    if (!selectedTenant) return [];

    return customers.filter(
      (c) => c.region === (selectedTenant === "PD US" ? "US" : "EU")
    );
  }, [selectedTenant, customers]);

  const onSubmit = async (values: FormValues) => {
    try {
      const selectedCustomer = filteredCustomers.find(
        (c) => c.id === values.businessServiceId
      );

      const businessService: PagerDutyBusinessService = {
        id: values.businessServiceId, // lowercase id for comparison
        tenant: values.tenant as PDTenant,
        reportingStartDate: values.reportingStartDate || null,
        reportingEndDate: values.reportingEndDate || null,
        name: selectedCustomer?.name || values.businessServiceId, // display name
      };

      const updateData = {
        ...customer,
      };

      const tools =
        updateData.toolsAndIntegrations ?? ({} as ToolsAndIntegrationsType);
      updateData.toolsAndIntegrations = tools;

      const existingServices: PagerDutyBusinessService[] = Array.isArray(
        tools.pagerDutyCustomers
      )
        ? tools.pagerDutyCustomers
        : [];

      // Check for duplicates using id (lowercase comparison)
      if (
        existingServices.some(
          (p) =>
            p.id === businessService.id && p.tenant === businessService.tenant
        )
      ) {
        showError({
          data: { detail: "This Business Service already exists" },
        } as any);

        return;
      }

      tools.pagerDutyCustomers = [...existingServices, businessService];

      await Axios().put(`/customers/${customerId}`, updateData);
      fetchCustomer();
      refetch();

      showSuccess(
        generateActionSuccessText(`PagerDuty Business Service`)()(`added`)()
      );
      setTimeout(() => {
        setIsSidebarOpen(false);
        methods.reset();
      }, 200);
    } catch (e: any) {
      showError(e.response);
    }
  };

  const businessServiceOptions = React.useMemo(
    () =>
      filteredCustomers.map((c) => ({
        value: c.id, // lowercase id for comparison/search
        label: c.name, // display name
      })),
    [filteredCustomers]
  );

  const handleSidebarOpen = () => {
    setIsSidebarOpen(true);
    fetchCustomers();
  };

  const prevTenantRef = React.useRef(selectedTenant);
  React.useEffect(() => {
    if (prevTenantRef.current !== selectedTenant && selectedTenant) {
      if (prevTenantRef.current && methods.getValues("businessServiceId")) {
        methods.setValue("businessServiceId", "");
      }
      prevTenantRef.current = selectedTenant;
    }
  }, [selectedTenant, methods]);

  return (
    <>
      <Button icon="plus" onClick={handleSidebarOpen}>
        Add PagerDuty Business Service
      </Button>

      <SidebarForm
        title="PagerDuty Business Service Configuration"
        width="36rem"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods as any}
        submit={onSubmit}
      >
        <SidebarDropdown
          name="tenant"
          label="Tenant"
          options={tenantOptions}
          key="tenant"
          disabled={customersLoading}
          required
        />

        <SidebarDropdown
          name="businessServiceId"
          label="Business Service"
          options={businessServiceOptions}
          key={`business-service-${selectedTenant}-${businessServiceOptions.length}`}
          disabled={customersLoading || !selectedTenant}
          required
        />

        <SidebarTextField
          name="reportingStartDate"
          label="Reporting Start Date"
          placeholder="YYYY-MM-DD"
          registerOptions={{ validate: validateDate }}
        />

        <SidebarTextField
          name="reportingEndDate"
          label="Reporting End Date"
          placeholder="YYYY-MM-DD"
          registerOptions={{ validate: validateDate }}
        />

        <SidebarFormButtons
          submitEnabled={
            !!(watcher.tenant && watcher.businessServiceId && !customersLoading)
          }
          onCancel={() => {
            setIsSidebarOpen(false);
            methods.reset();
          }}
        />
      </SidebarForm>
    </>
  );
}

export { AddPagerDutyBusinessService };
